import { useCallback, useMemo } from "react";
import { ManagerOptions, SocketOptions } from "socket.io-client";
import { useAtom, useSetAtom } from "jotai";
import { SOCKET_HOST, SOCKET_PATH } from "@/core/variables";
import { NLUSocket } from "@/core/socket";
import { User, BackgroundJob, WebSocketEventHandler, WebSocketEventType  } from "@/model";
import { getClientId } from "@/util/stringUtil";
import { backgroundJobsAtom } from "@/atom/backgroundJob";
import { isSocketConnectedAtom, socketAgentIdAtom, socketAtom, socketEventHandlersAtom } from "@/atom/websocket";


export const useWebsocket = () => {
    const [agentId, setAgentId] = useAtom(socketAgentIdAtom);
    const [socket, setSocket] = useAtom(socketAtom);
    const [isConnected, setIsConnected] = useAtom(isSocketConnectedAtom);
    const [eventHandlers, setEventHandlers] = useAtom(socketEventHandlersAtom);
    const setBackgroundJobs = useSetAtom(backgroundJobsAtom);

    const connect = useCallback((connectAgentId: number, loginUser: User, opts?: Partial<ManagerOptions & SocketOptions>) => {
        setAgentId(connectAgentId);
        if (socket) {
            socket.disconnect();
            setSocket(undefined);
        }

        const newSocket = new NLUSocket(`${SOCKET_HOST}`, `${SOCKET_PATH}`, opts);
        newSocket.connect(
            {
                client_id: getClientId(),
                agent_id: connectAgentId,
                email: loginUser.email,
                name: loginUser.name,
                uid: loginUser.user_id
            },
            () => {
                setIsConnected(true);
            }
        );

        newSocket.on("disconnected", () => {
            setIsConnected(false);
        });

        newSocket.on("agent_summary", (response: any) => {
            eventHandlers.agent_summary.forEach(func => func(response.data));
        });

        newSocket.on("background_job_status", (response: any) => {
            setBackgroundJobs(response.data);
            eventHandlers.background_job_status.forEach(func => func(response.data));
        });

        newSocket.on("background_job_ended", (response: any) => {
            eventHandlers.background_job_ended.forEach(func => func(response.data));
        });

        setSocket(newSocket);
    }, [socket, eventHandlers, setAgentId, setSocket, setIsConnected, setBackgroundJobs]);

    const disconnect = useCallback(() => {
        if (!socket) {
            return;
        }
        try {
            socket.disconnect();
        } catch (e) { }
        setIsConnected(false);
    }, [socket, setIsConnected]);

    const addEventListener = useCallback((
        event: WebSocketEventType, callback: WebSocketEventHandler
    ) => {
        setEventHandlers(prev => {
            prev[event].push(callback);
            return prev;
        });
    }, [setEventHandlers]);

    const removeEventListener = useCallback((
        event: WebSocketEventType, callback: WebSocketEventHandler
    ) => {
        setEventHandlers(prev => {
            const index = prev[event].indexOf(callback);
            if (index >= 0) {
                prev[event].splice(index, 1);
            }
            return prev;
        });

    }, [setEventHandlers]);

    const cancelBackgroundJob = useCallback((job: BackgroundJob) => {
        if (!socket) {
            return;
        }
        socket.emit("request_background_job_status", { agent_id: agentId, job: job, method: "cancel" });
    }, [socket, agentId]);

    const deleteBackgroundJob = useCallback((job: BackgroundJob) => {
        if (!socket) {
            return;
        }
        socket.emit("request_background_job_status", { agent_id: agentId, job: job, method: "delete" });
    }, [socket, agentId]);

    const clear = useCallback(() => {
        setAgentId(-1);
        disconnect();
        setSocket(undefined);
    }, [setAgentId, disconnect, setSocket]);

    return useMemo(() => {
        return {
            isConnected,
            connect,
            disconnect,
            addEventListener,
            removeEventListener,
            cancelBackgroundJob,
            deleteBackgroundJob,
            clear
        };
    }, [isConnected, connect, disconnect, addEventListener, removeEventListener, cancelBackgroundJob, deleteBackgroundJob, clear]);
};
